import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { Container } from './styledComponents';
import { getStoresForSelectedVariants } from './utils/getStoresForSelectedVariants';
import PerformancesContent from './components/PerformancesContent';
import PerformancesHeader, {
  DISPLAY_MODE,
  STORES_DROPDOWN_ID,
  VARIANTS_DROPDOWN_ID,
} from './components/PerformancesHeader';

/**
 * Sentinel id representing the main recipe (not a variant) inside the variants dropdown.
 * Maps to a `recipe_variant_id IS NULL` row in the composition of the recipe.
 */
export const MAIN_RECIPE_OPTION_ID = 'main';

const buildVariantsWithMain = (recipeVariants, mainRecipeName) => [
  {
    id: MAIN_RECIPE_OPTION_ID,
    name: mainRecipeName,
  },
  ...recipeVariants,
];

const Performances = (props) => {
  const {
    associatedProduct,
    clientChannels,
    compositions,
    currency,
    defaultChannelId,
    emptyStateLabel,
    isEmptyState,
    recipe,
    recipeVariants,
    stores,
  } = props;

  const variantsWithMain = useMemo(
    () => buildVariantsWithMain(recipeVariants, recipe?.name ?? ''),
    [recipeVariants, recipe?.name],
  );

  const [selectedVariants, setSelectedVariants] = useState(variantsWithMain);
  const [selectedStores, setSelectedStores] = useState(() =>
    getStoresForSelectedVariants({
      stores,
      recipeVariants,
      selectedVariants: variantsWithMain,
      mainRecipeOptionId: MAIN_RECIPE_OPTION_ID,
    }),
  );
  const [selectedChannels, setSelectedChannels] = useState(clientChannels);
  const [displayMode, setDisplayMode] = useState(DISPLAY_MODE.PERCENT);
  const [lastChangedFilter, setLastChangedFilter] = useState(null);

  const [filters, setFilters] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [, setApplyFilters] = useState(true);

  const availableStores = useMemo(
    () =>
      getStoresForSelectedVariants({
        stores,
        recipeVariants,
        selectedVariants,
        mainRecipeOptionId: MAIN_RECIPE_OPTION_ID,
      }),
    [stores, recipeVariants, selectedVariants],
  );

  useEffect(() => {
    setSelectedVariants(variantsWithMain);
  }, [recipe?.id, recipeVariants]);

  useEffect(() => {
    setSelectedStores(
      getStoresForSelectedVariants({
        stores,
        recipeVariants,
        selectedVariants: variantsWithMain,
        mainRecipeOptionId: MAIN_RECIPE_OPTION_ID,
      }),
    );
  }, [stores, recipe?.id, recipeVariants]);

  useEffect(() => {
    setSelectedChannels(clientChannels);
  }, [clientChannels]);

  useEffect(() => {
    setSelectedStores((current) => {
      const availableStoreIds = new Set(availableStores.map(({ id }) => id));
      const intersectedStores = current.filter(({ id }) => availableStoreIds.has(id));

      if (!intersectedStores.length && availableStores.length) {
        return availableStores;
      }

      if (intersectedStores.length === current.length) {
        return current;
      }

      return intersectedStores;
    });
  }, [availableStores]);

  // Keep the filter modal dropdowns in sync when variant selection narrows the store list.
  useEffect(() => {
    setFilters((current) => {
      if (!current) {
        return current;
      }

      const availableStoreIds = new Set(availableStores.map(({ id }) => id));
      const modalSelectedStores = selectedStores.filter(({ id }) => availableStoreIds.has(id));
      const storesForModal = modalSelectedStores.length ? modalSelectedStores : availableStores;

      return {
        ...current,
        [VARIANTS_DROPDOWN_ID]: {
          ...current[VARIANTS_DROPDOWN_ID],
          list: variantsWithMain,
          selectedItems: selectedVariants,
        },
        [STORES_DROPDOWN_ID]: {
          ...current[STORES_DROPDOWN_ID],
          list: availableStores,
          defaultSelectedItems: availableStores,
          selectedItems: storesForModal,
        },
      };
    });
  }, [availableStores, selectedStores, selectedVariants, variantsWithMain]);

  const areAdvancedFiltersDisabled = useMemo(() => !recipeVariants.length, [recipeVariants]);
  const isChannelDropdownDisabled = useMemo(() => clientChannels.length <= 1, [clientChannels]);

  // `DeepsightFiltersButton`'s save() iterates every dropdown's `setSelectedItems`, so both
  // handlers fire on each Apply regardless of which checkbox the user actually toggled. Without
  // a guard the last one called would always win — and `lastChangedFilter` would drift to the
  // wrong driver (e.g. 'store') even when the user only touched the variants list. Diff the
  // incoming selection against the current one and only flip `lastChangedFilter` on a real
  // change.
  const hasSelectionChanged = (current, next) => {
    if (current.length !== next.length) {
      return true;
    }

    const currentIds = new Set(current.map(({ id }) => id));

    return next.some(({ id }) => !currentIds.has(id));
  };

  const updateSelection = (filterKey, current, next, setSelection) => {
    setSelection(next);

    if (hasSelectionChanged(current, next)) {
      setLastChangedFilter(filterKey);
    }
  };

  const syncSelectedStoresWithVariants = (variants) => {
    const nextAvailableStores = getStoresForSelectedVariants({
      stores,
      recipeVariants,
      selectedVariants: variants,
      mainRecipeOptionId: MAIN_RECIPE_OPTION_ID,
    });
    const availableStoreIds = new Set(nextAvailableStores.map(({ id }) => id));
    const intersectedStores = selectedStores.filter(({ id }) => availableStoreIds.has(id));

    setSelectedStores(intersectedStores.length ? intersectedStores : nextAvailableStores);
  };

  const handleSelectedVariantsChange = (variants) => {
    updateSelection('variant', selectedVariants, variants, setSelectedVariants);
    syncSelectedStoresWithVariants(variants);
  };

  const handleSelectedStoresChange = (storesSelection) => {
    updateSelection('store', selectedStores, storesSelection, setSelectedStores);
  };

  return (
    <Container>
      <PerformancesHeader
        advancedFilters={advancedFilters}
        areAdvancedFiltersDisabled={areAdvancedFiltersDisabled}
        availableStores={availableStores}
        clientChannels={clientChannels}
        currency={currency}
        displayMode={displayMode}
        filters={filters}
        isChannelDropdownDisabled={isChannelDropdownDisabled}
        recipeVariants={variantsWithMain}
        selectedChannels={selectedChannels}
        selectedStores={selectedStores}
        selectedVariants={selectedVariants}
        setAdvancedFilters={setAdvancedFilters}
        setApplyFilters={setApplyFilters}
        setDisplayMode={setDisplayMode}
        setFilters={setFilters}
        setSelectedChannels={setSelectedChannels}
        setSelectedStores={handleSelectedStoresChange}
        setSelectedVariants={handleSelectedVariantsChange}
      />
      <PerformancesContent
        associatedProduct={associatedProduct}
        clientChannels={clientChannels}
        compositions={compositions}
        currency={currency}
        defaultChannelId={defaultChannelId}
        displayMode={displayMode}
        emptyStateLabel={emptyStateLabel}
        isEmptyState={isEmptyState}
        lastChangedFilter={lastChangedFilter}
        mainRecipeOptionId={MAIN_RECIPE_OPTION_ID}
        recipe={recipe}
        recipeVariants={recipeVariants}
        selectedChannels={selectedChannels}
        selectedStores={selectedStores}
        selectedVariants={selectedVariants}
        stores={stores}
      />
    </Container>
  );
};

Performances.propTypes = {
  associatedProduct: PropTypes.object,
  clientChannels: PropTypes.arrayOf(PropTypes.object),
  compositions: PropTypes.arrayOf(PropTypes.object),
  currency: PropTypes.object,
  defaultChannelId: PropTypes.string,
  // Drives the "association missing" empty state. Each host page decides the prerequisite:
  // a recipe needs an associated product, a product needs a mapped composition.
  emptyStateLabel: PropTypes.string,
  isEmptyState: PropTypes.bool,
  recipe: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    hasMultipleChannels: PropTypes.bool,
  }),
  recipeVariants: PropTypes.arrayOf(PropTypes.object),
  stores: PropTypes.arrayOf(PropTypes.object),
};

Performances.defaultProps = {
  associatedProduct: {},
  clientChannels: [],
  compositions: [],
  currency: null,
  defaultChannelId: null,
  emptyStateLabel: '',
  isEmptyState: false,
  recipe: {},
  recipeVariants: [],
  stores: [],
};

export default Performances;
